/* eslint-disable max-lines */
import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { UpsertPromptRequest } from "@/api/models"

import { ChevronRight, FolderOpen, Save, Search, Settings, Trash2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { useCurrentSessionType } from "@/hooks/use-current-session-type"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"

import { PromptFileSettings } from "./prompt-file-settings"
import { PromptGeneralSettings } from "./prompt-general-settings"
import { PromptLibrariesSettings } from "./prompt-libraries-settings"
import { PromptWebSearchSettings } from "./prompt-websearch-settings"
import { useDeletePrompt } from "../hooks/use-delete-prompt"
import { useGetPrompts } from "../hooks/use-get-prompts"
import { useUpsertPrompt } from "../hooks/use-upsert-prompt"

const useSettingsSections = () => {
  const { t } = useTranslation("", { keyPrefix: "prompt_settings" })

  return [
    {
      id: "general",
      label: t("general.title"),
      icon: Settings,
      description: t("general.description"),
    },
    {
      id: "libraries",
      label: t("libraries.title"),
      icon: FolderOpen,
      description: t("libraries.description"),
    },
    // Temporarily hidden due to persistence issues
    // {
    //   id: "files",
    //   label: t("files.title"),
    //   icon: FileText,
    //   description: t("files.description"),
    // },
    {
      id: "web-search",
      label: t("web_search.title"),
      icon: Search,
      description: t("web_search.description"),
    },
  ]
}

type PromptLibrariesDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function PromptLibrariesDialog({ open, onOpenChange }: PromptLibrariesDialogProps) {
  const { t } = useTranslation()
  const settingsService = SettingsService.getInstance()
  const userPolicy = settingsService.settings.user_policy

  // Determine sessionType from current route
  const sessionType = useCurrentSessionType()

  const [activeSection, setActiveSection] = useState("general")
  const [selectedPromptId, setSelectedPromptId] = useState<string>("")
  const [isCreatingNew, setIsCreatingNew] = useState(false)
  const [isCompanyPrompt, setIsCompanyPrompt] = useState(false)

  const { data: promptsData, isPending } = useGetPrompts()
  const upsertPromptMutation = useUpsertPrompt()
  const deletePromptMutation = useDeletePrompt()

  const prompts = promptsData?.prompts || []
  const settingsSections = useSettingsSections()

  // Local state for prompt data being edited
  const [updateData, setUpdateData] = useState<Partial<UpsertPromptRequest>>({
    title: "",
    content: "",
    defaultWebSearch: false,
    defaultModelId: null,
    defaultHighReasoning: false,
    useTrustedSources: false,
    trustedSourceUrls: [],
    trustedMode: "only",
    files: [],
    enabledIndexIds: [],
  })

  useEffect(() => {
    if (open) {
      setSelectedPromptId("create-new")
      setIsCompanyPrompt(false)
      setActiveSection("general")
      setUpdateData({
        title: "",
        content: "",
        defaultWebSearch: false,
        defaultModelId: null,
        defaultHighReasoning: false,
        useTrustedSources: false,
        trustedSourceUrls: [],
        trustedMode: "only",
        files: [],
        enabledIndexIds: [],
      })
      setIsCreatingNew(true)
    }
  }, [open])

  const handlePromptSelection = (value: string) => {
    setSelectedPromptId(value)

    if (value === "create-new") {
      setIsCreatingNew(true)
      setIsCompanyPrompt(false)
      setUpdateData({
        title: "",
        content: "",
        defaultWebSearch: false,
        defaultModelId: null,
        defaultHighReasoning: false,
        useTrustedSources: false,
        trustedSourceUrls: [],
        trustedMode: "only",
        files: [],
        enabledIndexIds: [],
      })
    } else {
      setIsCreatingNew(false)
      const selectedPrompt = prompts.find((p) => p.id === value)
      if (selectedPrompt) {
        setIsCompanyPrompt(selectedPrompt.type === "company")
        setUpdateData({
          title: selectedPrompt.title,
          content: selectedPrompt.content,
          defaultWebSearch: selectedPrompt.defaultWebSearch ?? false,
          defaultModelId: selectedPrompt.defaultModelId ?? null,
          defaultHighReasoning: selectedPrompt.defaultHighReasoning ?? false,
          useTrustedSources: selectedPrompt.useTrustedSources ?? false,
          trustedSourceUrls: selectedPrompt.trustedSourceUrls ?? [],
          trustedMode: selectedPrompt.trustedMode ?? "only",
          files: selectedPrompt.files ?? [],
          enabledIndexIds: selectedPrompt.enabledIndexIds ?? [],
        })
      }
    }
  }

  const handleUpdate = (data: Partial<UpsertPromptRequest>) => {
    setUpdateData((prev) => ({ ...prev, ...data }))
  }

  const handleSave = async () => {
    if (!updateData.title || !updateData.content) {
      toast.error(t("prompts.messages.title_and_content_required"))
      return
    }

    try {
      const promptData: UpsertPromptRequest = {
        id: isCreatingNew ? undefined : selectedPromptId,
        title: updateData.title,
        content: updateData.content,
        type: (isCompanyPrompt ? "company" : "personal") as "company" | "personal",
        defaultWebSearch: updateData.defaultWebSearch,
        defaultModelId: updateData.defaultModelId,
        defaultHighReasoning: updateData.defaultHighReasoning,
        useTrustedSources: updateData.useTrustedSources,
        trustedSourceUrls: updateData.trustedSourceUrls,
        trustedMode: updateData.trustedMode,
        files: updateData.files,
        enabledIndexIds: updateData.enabledIndexIds,
      }

      await upsertPromptMutation.mutateAsync(promptData, {
        onSuccess: (data) => {
          toast.success(
            isCreatingNew
              ? t("prompts.messages.created_successfully")
              : t("prompts.messages.updated_successfully")
          )
          if (isCreatingNew) {
            setSelectedPromptId(data.id)
            setIsCreatingNew(false)
          }
        },
      })
    } catch {
      toast.error(t("prompts.messages.failed_to_save"))
    }
  }

  const handleDelete = async () => {
    if (isCreatingNew) return

    if (confirm(t("prompts.messages.confirm_delete"))) {
      await deletePromptMutation.mutateAsync(
        {
          promptId: selectedPromptId,
          type: isCompanyPrompt ? "company" : "personal",
        },
        {
          onSuccess: () => {
            setSelectedPromptId("create-new")
            setIsCreatingNew(true)
            setUpdateData({
              title: "",
              content: "",
              defaultWebSearch: false,
              defaultModelId: null,
              defaultHighReasoning: false,
              useTrustedSources: false,
              trustedSourceUrls: [],
              trustedMode: "only",
              files: [],
              enabledIndexIds: [],
            })
            toast.success(t("prompts.messages.deleted_successfully"))
          },
        }
      )
    }
  }

  const handleFilesAdd = (files: File[]) => {
    const newFiles = files.map((f) => ({
      filename: f.name,
      uploaded_at: new Date().toISOString(),
    }))
    const existingFiles = updateData.files || []
    handleUpdate({ files: [...existingFiles, ...newFiles] })
  }

  const handleFileRemove = (filename: string) => {
    const files = (updateData.files || []).filter((f) => f.filename !== filename)
    handleUpdate({ files })
  }

  const renderSectionContent = () => {
    switch (activeSection) {
      case "general":
        return (
          <PromptGeneralSettings
            sessionType={sessionType}
            updateData={updateData}
            onUpdate={handleUpdate}
          />
        )

      case "files":
        return (
          <PromptFileSettings
            files={updateData.files || []}
            onFileRemove={handleFileRemove}
            onFilesAdd={handleFilesAdd}
          />
        )

      case "libraries":
        return (
          <PromptLibrariesSettings
            sessionType={sessionType}
            updateData={updateData}
            onUpdate={handleUpdate}
          />
        )

      case "web-search":
        return <PromptWebSearchSettings updateData={updateData} onUpdate={handleUpdate} />

      default:
        return null
    }
  }

  const personalPrompts = prompts.filter((p) => p.type === "personal")
  const companyPrompts = prompts.filter((p) => p.type === "company")

  const isAnyMutationPending = upsertPromptMutation.isPending || deletePromptMutation.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[100dvh] flex-col overflow-hidden p-0 sm:max-w-4xl md:h-full md:max-h-[90vh]">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:grid lg:grid-cols-[280px_1fr]">
          {/* Header - Mobile Only */}
          <div className="flex-shrink-0 border-b px-4 py-4 lg:hidden">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">
                {t("prompts.prompt_libraries")}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground text-sm">
                {t("prompts.prompt_libraries_description")}
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Mobile Tab Navigation */}
          <div className="bg-muted/30 flex-shrink-0 border-b lg:hidden">
            <nav className="flex flex-wrap">
              {settingsSections.map((section) => {
                const Icon = section.icon
                const isActive = activeSection === section.id

                return (
                  <button
                    key={section.id}
                    className={cn(
                      "flex flex-1 items-center justify-center gap-2 border-b-2 px-3 py-3 text-sm transition-colors",
                      "hover:bg-accent hover:text-accent-foreground",
                      "min-w-0 basis-1/3",
                      isActive
                        ? "border-primary bg-accent text-accent-foreground font-medium"
                        : "border-transparent"
                    )}
                    onClick={() => setActiveSection(section.id)}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate text-xs sm:text-sm">{section.label}</span>
                  </button>
                )
              })}
            </nav>
          </div>

          {/* Desktop Sidebar Navigation */}
          <div className="bg-muted/30 hidden min-h-0 flex-col border-r p-6 lg:flex">
            <DialogHeader className="mb-6">
              <DialogTitle className="text-lg font-semibold">
                {t("prompts.prompt_libraries")}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground text-sm">
                {t("prompts.prompt_libraries_description")}
              </DialogDescription>
            </DialogHeader>

            {/* Prompt Selection */}
            <div className="mb-6 space-y-2">
              <Label htmlFor="prompt-select">{t("prompts.select_prompt")}</Label>
              <Select
                disabled={isAnyMutationPending}
                value={selectedPromptId}
                onValueChange={handlePromptSelection}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      isPending
                        ? t("prompts.loading_prompts")
                        : t("prompts.choose_prompt_or_create_new")
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem className="text-primary font-medium" value="create-new">
                    + {t("prompts.create_new_prompt")}
                  </SelectItem>
                  {personalPrompts.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>{t("prompts.personal_prompts")}</SelectLabel>
                      {personalPrompts.map((prompt) => (
                        <SelectItem key={prompt.id} value={prompt.id}>
                          {prompt.title}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                  {userPolicy.is_prompt_admin && companyPrompts.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>{t("prompts.company_prompts")}</SelectLabel>
                      {companyPrompts.map((prompt) => (
                        <SelectItem key={prompt.id} value={prompt.id}>
                          {prompt.title}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Admin Toggle for Company Prompt */}
            {userPolicy.is_prompt_admin && (
              <div className="mb-6 flex items-center justify-between rounded-md border p-3">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">
                    {t("prompts.form.company_prompt_label")}
                  </Label>
                  <div className="text-muted-foreground text-xs">
                    {t("prompts.form.company_prompt_description")}
                  </div>
                </div>
                <Switch
                  checked={isCompanyPrompt}
                  disabled={isAnyMutationPending}
                  onCheckedChange={setIsCompanyPrompt}
                />
              </div>
            )}

            <div className="min-h-0 flex-1 overflow-y-auto">
              <nav className="space-y-1">
                {settingsSections.map((section) => {
                  const Icon = section.icon
                  const isActive = activeSection === section.id

                  return (
                    <button
                      key={section.id}
                      className={cn(
                        "group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors",
                        "hover:bg-accent hover:text-accent-foreground",
                        isActive && "bg-accent text-accent-foreground font-medium"
                      )}
                      onClick={() => setActiveSection(section.id)}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{section.label}</div>
                        <div
                          className={cn(
                            "text-secondary-foreground group-hover:text-accent-foreground truncate text-xs",
                            isActive && "text-accent-foreground"
                          )}
                        >
                          {section.description}
                        </div>
                      </div>
                      {isActive && <ChevronRight className="h-4 w-4 shrink-0" />}
                    </button>
                  )
                })}
              </nav>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {/* Desktop Header */}
            <div className="hidden flex-shrink-0 border-b px-6 py-4 lg:block">
              <h3 className="text-lg font-semibold">
                {settingsSections.find((s) => s.id === activeSection)?.label}
              </h3>
              <p className="text-muted-foreground mt-1 text-sm">
                {settingsSections.find((s) => s.id === activeSection)?.description}
              </p>
            </div>

            {/* Mobile Header */}
            <div className="flex-shrink-0 border-b px-4 py-3 lg:hidden">
              <h3 className="font-semibold">
                {settingsSections.find((s) => s.id === activeSection)?.label}
              </h3>
              <p className="text-muted-foreground mt-1 text-xs">
                {settingsSections.find((s) => s.id === activeSection)?.description}
              </p>
            </div>

            {/* Scrollable Content */}
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 lg:p-6">
              <div className="mx-auto max-w-2xl">{renderSectionContent()}</div>
            </div>

            {/* Action Buttons - Fixed at bottom */}
            <div className="bg-background flex-shrink-0 border-t px-4 py-4 lg:px-6">
              <div className="mx-auto flex max-w-2xl justify-between gap-3">
                <Button
                  className="flex items-center gap-2"
                  disabled={isCreatingNew || deletePromptMutation.isPending}
                  type="button"
                  variant="destructive"
                  onClick={handleDelete}
                >
                  <Trash2 className="h-4 w-4" />
                  {deletePromptMutation.isPending
                    ? t("prompts.form.deleting")
                    : t("prompts.form.delete")}
                </Button>

                <Button
                  className="flex items-center gap-2"
                  disabled={isAnyMutationPending || !updateData.title || !updateData.content}
                  type="button"
                  onClick={handleSave}
                >
                  <Save className="h-4 w-4" />
                  {(() => {
                    if (upsertPromptMutation.isPending) {
                      return isCreatingNew ? t("prompts.form.creating") : t("prompts.form.saving")
                    }
                    return isCreatingNew ? t("prompts.form.create") : t("prompts.form.save")
                  })()}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
